import { useState } from "react";
import { useCart } from "../context/CartContext";
import { X, Plus, Minus, Trash2, ShoppingBag } from "lucide-react";
import { createCheckoutSession } from "../lib/api";
import { toast } from "sonner";

export default function CartSheet() {
  const { open, setOpen, items, updateQty, removeItem, subtotal } = useCart();
  const [loading, setLoading] = useState(false);

  const checkout = async () => {
    if (items.length === 0) return;
    setLoading(true);
    try {
      const origin = window.location.origin;
      const res = await createCheckoutSession(items, origin);
      window.location.href = res.url;
    } catch (e) {
      console.error(e);
      toast.error("Checkout failed. Please try again.");
      setLoading(false);
    }
  };

  return (
    <>
      {open && (
        <div
          className="fixed inset-0 bg-black/40 z-50"
          onClick={() => setOpen(false)}
          data-testid="cart-backdrop"
        />
      )}
      <aside
        className={`fixed top-0 right-0 h-full w-full sm:w-[440px] bg-[#FFFDF0] border-l-4 border-black z-50 transform transition-transform duration-300 ${
          open ? "translate-x-0" : "translate-x-full"
        } flex flex-col`}
        data-testid="cart-sheet"
      >
        <div className="flex items-center justify-between p-5 border-b-4 border-black">
          <h2 className="font-display text-2xl flex items-center gap-2">
            <ShoppingBag strokeWidth={3} size={24} /> Your Cart
          </h2>
          <button
            onClick={() => setOpen(false)}
            className="border-3 border-black rounded-full p-2 hover:bg-[#FFD500]"
            style={{ borderWidth: 3 }}
            data-testid="cart-close-button"
            aria-label="Close cart"
          >
            <X strokeWidth={3} size={20} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {items.length === 0 && (
            <div className="text-center py-16" data-testid="cart-empty">
              <p className="font-display text-2xl mb-2">Cart is empty</p>
              <p className="text-sm opacity-70">
                Add some Playworld goodies to start your adventure!
              </p>
            </div>
          )}

          {items.map((it) => (
            <div
              key={it.key}
              className="pw-card p-3 flex gap-3"
              data-testid={`cart-item-${it.product_id}`}
            >
              <img
                src={it.image}
                alt={it.name}
                className="w-20 h-20 object-cover rounded-lg border-3 border-black"
                style={{ borderWidth: 3 }}
              />
              <div className="flex-1 min-w-0">
                <p className="font-bold leading-tight truncate">{it.name}</p>
                {it.size && (
                  <p className="text-xs opacity-70">Size: {it.size}</p>
                )}
                <p className="font-display text-lg mt-1">${it.price.toFixed(2)}</p>
                <div className="flex items-center gap-2 mt-2">
                  <button
                    className="border-2 border-black rounded-full w-7 h-7 inline-flex items-center justify-center hover:bg-[#FFD500]"
                    onClick={() => updateQty(it.key, it.quantity - 1)}
                    data-testid={`cart-decrease-${it.product_id}`}
                    aria-label="Decrease quantity"
                  >
                    <Minus size={14} strokeWidth={3} />
                  </button>
                  <span className="font-bold w-6 text-center" data-testid={`cart-qty-${it.product_id}`}>
                    {it.quantity}
                  </span>
                  <button
                    className="border-2 border-black rounded-full w-7 h-7 inline-flex items-center justify-center hover:bg-[#FFD500]"
                    onClick={() => updateQty(it.key, it.quantity + 1)}
                    data-testid={`cart-increase-${it.product_id}`}
                    aria-label="Increase quantity"
                  >
                    <Plus size={14} strokeWidth={3} />
                  </button>
                  <button
                    className="ml-auto text-[#FF2E93] hover:underline inline-flex items-center gap-1 text-sm font-bold"
                    onClick={() => removeItem(it.key)}
                    data-testid={`cart-remove-${it.product_id}`}
                  >
                    <Trash2 size={14} strokeWidth={3} /> Remove
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="border-t-4 border-black p-5 bg-white">
          <div className="flex items-center justify-between mb-4">
            <span className="font-bold text-lg">Subtotal</span>
            <span className="font-display text-2xl" data-testid="cart-subtotal">
              ${subtotal.toFixed(2)}
            </span>
          </div>
          <button
            onClick={checkout}
            disabled={items.length === 0 || loading}
            className="pw-btn w-full disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="checkout-button"
          >
            {loading ? "Redirecting..." : "Checkout"}
          </button>
          <p className="text-xs opacity-60 text-center mt-3">
            Secure checkout powered by Stripe
          </p>
        </div>
      </aside>
    </>
  );
}
